import React, { useRef, useImperativeHandle, useEffect } from 'react';
import { Form, Input } from 'antd';
import { DEFAULT_LOCALE_CODE } from '../../../const'; 
import LocaleSelector from '../LocaleSelector/LocaleSelector';
import TinyMCE from '../../../components/TinyMCE/TinyMCE';
import './styles.less';

const FormLocales = ( { form, modifiedCallback, ref } ) => {
    const TinyMCERef = useRef( null );  

    useImperativeHandle( ref, () => ( {
        reset( changeLocaleToDefault ) {  
            form.setFieldsValue( { localesModified: [] } ); 
            form.setFieldsValue( { localesAreModified: false } ); 
            if ( changeLocaleToDefault ) {
                form.setFieldsValue( { locale: DEFAULT_LOCALE_CODE } ); 
            }
            updateLocale();
            updateLocalesForSaving();
            TinyMCERef.current?.resetHadUserInput(); 
            modifiedCallback( false );
        }
    } ) );
 
    /***************************************
     *  ANCHOR  Modified locales handling
     ***************************************/
    
    const updateLocalesForSaving = () => {
        const isModified = form.getFieldValue( 'localesAreModified' );
        const locales = form.getFieldValue( 'locales' );
        
        if ( !isModified ) {
            form.setFieldsValue( { localesForSaving: JSON.parse( JSON.stringify( locales ) ) } );
        } else {
            const localesModified = form.getFieldValue( 'localesModified' );
            const result = JSON.parse( JSON.stringify( locales ) );

            localesModified.forEach( i => { 
                const found = result.find( r => r.locale === i.locale );
                if ( found ) {
                    found.name = i.name;
                    found.description = i.description;
                } else {
                    result.push( { ...i } );
                }
            } );

            form.setFieldsValue( { localesForSaving: result.filter( i => i.name !== '' || i.description !== '' ) } );
        }
    };

    const updateLocalesAreModifiedForName = () => {
        const localesModified = form.getFieldValue( 'localesModified' ).length > 0;
        form.setFieldsValue( { localesAreModified: localesModified } );  
        modifiedCallback( localesModified  );
        updateLocalesForSaving();
    };

    const updateLocalesAreModifiedForDescription = () => {
        let localesModified = form.getFieldValue( 'localesModified' ).length > 0;
        
        // If not modified by user set to NOT modified
        if ( !TinyMCERef.current?.hadUserInput() ) { 
            localesModified = false;
        }
        
        form.setFieldsValue( { localesAreModified: localesModified } );  
        modifiedCallback( localesModified );
        updateLocalesForSaving();
    };

    // Removes locales from localesModified that are not truly modified
    const sanitizeLocalesModified = ( localesMod ) => {
        const locales = form.getFieldValue( 'locales' );

        const result = localesMod.filter( i => {
            const found = locales.find( l => l.locale === i.locale );
            
            if ( found ) { 
                if ( i.name === found.name && i.description === found.description ) {
                    return false;
                }
            } else {
                if ( i.name === '' && i.description === '' ) {
                    return false;
                }
            }

            return true;
        } );

        return result;
    };

    /***************************************
     *  ANCHOR Handle change selectedLocale
     ***************************************/
   
    const selectedLocale = Form.useWatch( 'locale', form );

    const updateLocale = () => {
        const localesModified = form.getFieldValue( 'localesModified' );
        let newLocale = localesModified.find( i => i.locale === selectedLocale );

        if ( !newLocale ) {
            const locales = form.getFieldValue( 'locales' );
            newLocale = locales.find( i => i.locale === selectedLocale );
        }

        form.setFieldsValue( { name: newLocale?.name || '' } ); 
        form.setFieldsValue ( { description: newLocale?.description || '' } );
    };

    useEffect( () => {
        updateLocale();
    }, [ selectedLocale ] ); 

    /***************************************
     *  ANCHOR Handle name change
     ***************************************/
   
    const handleNameChange = () => {
        if ( !selectedLocale ) { return; }
        
        const newName = form.getFieldValue( 'name' );
        const localesModified = form.getFieldValue( 'localesModified' );
        const newLocale = localesModified.find( i => i.locale === selectedLocale );

        // If there is an existing localesModified add changes to it
        if ( newLocale ) {
            const updatedLocales = [ 
                ...localesModified.filter( loc => loc.locale !== selectedLocale ), 
                {
                    ...newLocale,
                    name: newName
                } 
            ];
            
            form.setFieldsValue( { localesModified: sanitizeLocalesModified( updatedLocales ) } ); 
            updateLocalesAreModifiedForName();
        } else {
            // Copy from locales to localesModified and add changes to it
            const locales = form.getFieldValue( 'locales' );
            let newLocale = locales.find( i => i.locale === selectedLocale );

            if ( !newLocale ) {
                newLocale = {
                    locale: selectedLocale, 
                    name: '', 
                    description: ''
                };
            }

            const updatedLocales = [ 
                ...localesModified, 
                {
                    ...newLocale,
                    name: newName
                } 
            ];
    
            form.setFieldsValue( { localesModified: sanitizeLocalesModified( updatedLocales ) } ); 
            updateLocalesAreModifiedForName();
        }
    };
    
    /***************************************
     *  ANCHOR Handle description change
     ***************************************/
   
    const description = Form.useWatch( 'description', form );

    useEffect( () => {
        if ( !selectedLocale ) { return; }

        const newDescription = form.getFieldValue( 'description' );
        const localesModified = form.getFieldValue( 'localesModified' );
        const newLocale = localesModified.find( i => i.locale === selectedLocale );

        // If there is an existing localesModified add changes to it
        if ( newLocale ) {
            const updatedLocales = [ 
                ...localesModified.filter( loc => loc.locale !== selectedLocale ), 
                {
                    ...newLocale,
                    description: newDescription
                } 
            ];
            
            form.setFieldsValue( { localesModified: sanitizeLocalesModified( updatedLocales ) } ); 
            updateLocalesAreModifiedForDescription();
        } else {
            // Copy from locales to localesModified and add changes to it
            const locales = form.getFieldValue( 'locales' );
            let newLocale = locales.find( i => i.locale === selectedLocale );

            if ( !newLocale ) {
                newLocale = {
                    locale: selectedLocale, 
                    name: '', 
                    description: ''
                };
            }

            const updatedLocales = [ 
                ...localesModified, 
                {
                    ...newLocale,
                    description: newDescription
                } 
            ];
    
            form.setFieldsValue( { localesModified: sanitizeLocalesModified( updatedLocales ) } ); 
            updateLocalesAreModifiedForDescription();
        }
    }, [ description ] ); 

    /*************************************
     *  ANCHOR Render
     *************************************/

    return (
        <div className="form-item-locales">
            <Form.Item 
                label="Language"
                name="locale"
                valuePropName="initialValue"
            >
                <LocaleSelector />
            </Form.Item> 

            <Form.Item
                name="name"
                label="Name"
                onChange={ handleNameChange }
            >
                <Input placeholder="Name" />
            </Form.Item> 
 
            <Form.Item
                name="description"
                label="Description"
            >
                <TinyMCE ref={ TinyMCERef } />
            </Form.Item> 
        </div>
    );
};

export default FormLocales;
